import { useState, useEffect, useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { useAuth } from '../../context/AuthContext';
import portalService from '../../services/portal';
import { useClickOutside } from '../../hooks/useClickOutside';
import type { EmailSignature } from '../../types/portal';
import styles from './PortalSettingsPage.module.css';

type ViewMode = 'visual' | 'html';

/** Max image file size in bytes (500 KB). */
const MAX_IMAGE_SIZE = 500 * 1024;

/** Allowed MIME types for signature images. */
const ALLOWED_IMAGE_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
]);

const PortalSettingsPage: React.FC = () => {
  const { user } = useAuth();

  const [signature, setSignature] = useState<EmailSignature | null>(null);
  const [sigName, setSigName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('visual');
  const [htmlSource, setHtmlSource] = useState('');
  const [showImageMenu, setShowImageMenu] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageMenuRef = useRef<HTMLDivElement>(null);

  // TipTap editor for visual editing
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false }),
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          style: 'max-width: 100%; height: auto;',
        },
        resize: {
          enabled: true,
          directions: ['top-right', 'bottom-right', 'bottom-left', 'top-left'],
          minWidth: 30,
          minHeight: 30,
          alwaysPreserveAspectRatio: true,
        },
      }),
    ],
    content: '',
    onUpdate: ({ editor: ed }) => {
      setHtmlSource(ed.getHTML());
    },
  });

  const closeImageMenu = useCallback(() => setShowImageMenu(false), []);
  useClickOutside(imageMenuRef, closeImageMenu, showImageMenu);

  // Load the default signature (auto-creates if needed)
  const loadSignature = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const sig = await portalService.getOrCreateDefaultSignature();
      setSignature(sig);
      setSigName(sig.name);
      setHtmlSource(sig.signature_html);
      if (editor) {
        editor.commands.setContent(sig.signature_html);
      }
    } catch (err) {
      setError('Failed to load signature. Please try again.');
      console.error('Load signature error:', err);
    } finally {
      setLoading(false);
    }
  }, [editor]);

  useEffect(() => {
    if (editor) {
      loadSignature();
    }
  }, [editor, loadSignature]);

  // When switching from HTML source back to visual, sync content
  const handleViewModeChange = (mode: ViewMode) => {
    if (mode === 'visual' && editor) {
      editor.commands.setContent(htmlSource);
    }
    setViewMode(mode);
  };

  // Insert image from URL
  const handleInsertImageUrl = () => {
    if (!editor) return;
    setShowImageMenu(false);

    const url = window.prompt('Enter image URL:');
    if (!url) return;

    // Basic URL validation
    try {
      new URL(url);
    } catch {
      setError('Please enter a valid URL.');
      setTimeout(() => setError(null), 3000);
      return;
    }

    const alt = window.prompt('Enter alt text (optional):') ?? '';
    editor.chain().focus().setImage({ src: url, alt }).run();
  };

  // Upload image file to GCS and insert
  const handleImageFileSelected = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!editor) return;
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset file input so the same file can be re-selected
    e.target.value = '';

    if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
      setError('Unsupported image type. Use PNG, JPEG, GIF, or WebP.');
      setTimeout(() => setError(null), 4000);
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      setError('Image too large. Maximum size is 500 KB.');
      setTimeout(() => setError(null), 4000);
      return;
    }

    try {
      setUploading(true);
      setError(null);
      const publicUrl = await portalService.uploadSignatureImage(file);

      const alt = file.name.replace(/\.[^.]+$/, '');
      editor.chain().focus().setImage({ src: publicUrl, alt }).run();
      setSuccess('Image uploaded successfully.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Image upload error:', err);
      const detail =
        err instanceof Error && err.message
          ? err.message
          : 'Unknown error';
      setError(`Failed to upload image: ${detail}`);
      setTimeout(() => setError(null), 6000);
    } finally {
      setUploading(false);
    }
  };

  // Save the signature
  const handleSave = async () => {
    if (!signature) return;
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const currentHtml = viewMode === 'visual' && editor
        ? editor.getHTML()
        : htmlSource;

      const updated = await portalService.updateSignature(signature.id, {
        name: sigName,
        signature_html: currentHtml,
      });
      setSignature(updated);
      setHtmlSource(updated.signature_html);
      if (editor) {
        editor.commands.setContent(updated.signature_html);
      }
      setSuccess('Signature saved successfully.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to save signature. Please try again.');
      console.error('Save signature error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Reset to the corporate-mandate format (server re-renders from the user record)
  const handleReset = async () => {
    if (!signature) return;
    try {
      setSaving(true);
      setError(null);
      const fresh = await portalService.resetDefaultSignature();
      setSignature(fresh);
      setSigName(fresh.name);
      setHtmlSource(fresh.signature_html);
      if (editor) {
        editor.commands.setContent(fresh.signature_html);
      }
      setSuccess('Signature reset to the corporate format.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to reset signature.');
      console.error('Reset error:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Loading settings...</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Settings</h1>
      </div>

      {/* User info card */}
      {user && (
        <div className={styles.userInfo}>
          <span>
            <span className={styles.userInfoLabel}>Name: </span>
            {user.name}
          </span>
          <span>
            <span className={styles.userInfoLabel}>Email: </span>
            {user.email}
          </span>
          <span>
            <span className={styles.userInfoLabel}>Role: </span>
            {user.role}
          </span>
        </div>
      )}

      {success && <div className={styles.successMsg}>{success}</div>}
      {error && <div className={styles.errorMsg}>{error}</div>}

      {/* Signature Editor Section */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div>
            <h2 className={styles.sectionTitle}>Email Signature</h2>
            <p className={styles.sectionDesc}>
              This signature is automatically appended to emails you send from the portal.
            </p>
          </div>
        </div>

        {/* Signature name */}
        <div className={styles.nameRow}>
          <label className={styles.nameLabel} htmlFor="sig-name">
            Name:
          </label>
          <input
            id="sig-name"
            className={styles.nameInput}
            value={sigName}
            onChange={(e) => setSigName(e.target.value)}
            placeholder="Signature name"
          />
        </div>

        {/* View mode toggle */}
        <div className={styles.sourceToggle}>
          <button
            className={`${styles.sourceBtn} ${viewMode === 'visual' ? styles.activeTab : ''}`}
            onClick={() => handleViewModeChange('visual')}
          >
            Visual Editor
          </button>
          <button
            className={`${styles.sourceBtn} ${viewMode === 'html' ? styles.activeTab : ''}`}
            onClick={() => handleViewModeChange('html')}
          >
            HTML Source
          </button>
        </div>

        {viewMode === 'visual' && editor ? (
          <div className={styles.editorWrapper}>
            {/* Toolbar */}
            <div className={styles.toolbar}>
              <button
                className={`${styles.toolbarBtn} ${editor.isActive('bold') ? styles.active : ''}`}
                onClick={() => editor.chain().focus().toggleBold().run()}
                title="Bold"
              >
                B
              </button>
              <button
                className={`${styles.toolbarBtn} ${editor.isActive('italic') ? styles.active : ''}`}
                onClick={() => editor.chain().focus().toggleItalic().run()}
                title="Italic"
                style={{ fontStyle: 'italic' }}
              >
                I
              </button>
              <button
                className={`${styles.toolbarBtn} ${editor.isActive('underline') ? styles.active : ''}`}
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                title="Underline"
                style={{ textDecoration: 'underline' }}
              >
                U
              </button>

              <div className={styles.toolbarSep} />

              <button
                className={`${styles.toolbarBtn} ${editor.isActive('link') ? styles.active : ''}`}
                onClick={() => {
                  if (editor.isActive('link')) {
                    editor.chain().focus().unsetLink().run();
                  } else {
                    const url = window.prompt('Enter URL:');
                    if (url) {
                      editor.chain().focus().setLink({ href: url }).run();
                    }
                  }
                }}
                title="Link"
              >
                🔗
              </button>

              <div className={styles.toolbarSep} />

              {/* Image insertion dropdown */}
              <div className={styles.imageDropdownContainer} ref={imageMenuRef}>
                <button
                  className={`${styles.toolbarBtn} ${showImageMenu ? styles.active : ''}`}
                  onClick={() => setShowImageMenu((prev) => !prev)}
                  title="Insert Image"
                  disabled={uploading}
                >
                  {uploading ? '...' : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                  )}
                </button>
                {showImageMenu && (
                  <div className={styles.imageMenu}>
                    <button
                      className={styles.imageMenuItem}
                      onClick={handleInsertImageUrl}
                    >
                      From URL
                    </button>
                    <button
                      className={styles.imageMenuItem}
                      onClick={() => {
                        setShowImageMenu(false);
                        fileInputRef.current?.click();
                      }}
                    >
                      Upload File
                    </button>
                  </div>
                )}
              </div>

              {/* Hidden file input for image upload */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                style={{ display: 'none' }}
                onChange={handleImageFileSelected}
              />

              <div className={styles.toolbarSep} />

              <button
                className={styles.toolbarBtn}
                onClick={() => editor.chain().focus().undo().run()}
                disabled={!editor.can().undo()}
                title="Undo"
              >
                ↩
              </button>
              <button
                className={styles.toolbarBtn}
                onClick={() => editor.chain().focus().redo().run()}
                disabled={!editor.can().redo()}
                title="Redo"
              >
                ↪
              </button>
            </div>

            {/* Uploading indicator */}
            {uploading && (
              <div className={styles.uploadingBar}>
                Uploading image...
              </div>
            )}

            {/* Editor area */}
            <div className={styles.editorContent}>
              <EditorContent editor={editor} />
            </div>
          </div>
        ) : (
          <textarea
            className={styles.htmlSource}
            value={htmlSource}
            onChange={(e) => setHtmlSource(e.target.value)}
            placeholder="Enter HTML signature here..."
            spellCheck={false}
          />
        )}

        {/* Live preview */}
        <div className={styles.previewLabel}>Preview</div>
        <div
          className={styles.preview}
          dangerouslySetInnerHTML={{ __html: htmlSource }}
        />

        {/* Actions */}
        <div className={styles.actions} style={{ marginTop: '1rem' }}>
          <button
            className={styles.resetBtn}
            onClick={handleReset}
            disabled={saving}
          >
            Reset to Default
          </button>
          <button
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Signature'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PortalSettingsPage;
